/**
 * Ekip › Organizasyon: who does what and how work flows, drawn from live data (the
 * twin-organizasyon page made into a screen). Tiers: Sahip, Yönetim, Departmanlar (v0.1.150: the company in
 * departments, each with its manager and staff, from GET /team/organization), Uygulama, Sistemler. Agent nodes come from the snapshot (state, model, budget, what it waits
 * for); the executor from the system state (dry run, kill switch); the system nodes
 * from the settings (connection statuses, flags, version). A node opens its detail
 * under the chart. The flows list each job's steps with its owner and approval tier,
 * and say what is still closed and why.
 */

import { useEffect, useId, useState } from 'react';
import { ORG_RUNTIME_LABELS, ORG_STATE_LABELS, type OrgDepartment, type OrgMember } from '../api/organization.js';
import type { Connection, Settings } from '../api/settings.js';
import type { Agent, Snapshot } from '../api/types.js';
import { usePanel } from '../app/context.js';
import { useOrganization } from '../app/organization.js';
import { Link } from '../app/router.js';
import { agentCounts } from '../app/selectors.js';
import { formatUsd, formatUsdPair } from '../lib/format.js';
import { Icon } from './icons.js';
import { Office } from './office.js';
import { SectionHeader } from './primitives.js';

type NodeState = 'on' | 'wait' | 'off' | 'need' | 'trial';
type Tier = 0 | 1 | 2 | 3 | 4;

interface OrgNode {
  /** The id the Kişi page takes (a panel agent id or a human id); nothing for a placeholder role. */
  person?: string;
  id: string;
  tier: Tier;
  name: string;
  role: string;
  state: NodeState;
  badge: string;
  meta: string;
  human?: boolean;
  details: [string, string][];
}

const TIERS: { tier: Tier; label: string; one: boolean }[] = [
  { tier: 0, label: 'Sahip', one: true },
  { tier: 1, label: 'Yönetim', one: true },
  { tier: 2, label: 'Departmanlar', one: false },
  { tier: 3, label: 'Uygulama', one: true },
  { tier: 4, label: 'Sistemler', one: false },
];

const FILTERS: { id: 'all' | NodeState; label: string }[] = [
  { id: 'all', label: 'Hepsi' },
  { id: 'on', label: 'Çalışan' },
  { id: 'wait', label: 'Bekleyen' },
  { id: 'need', label: 'Senden' },
  { id: 'off', label: 'Kapalı' },
];

/** What each agent is for; the live part (state, model, budget, waiting reason) comes from the snapshot. */
const AGENT_COPY: Readonly<Record<string, { role: string; duty: string; rule?: string; output?: string }>> = {
  gm: {
    role: 'Yönetici ajan',
    duty: '07:30 sabah planı (rakamları okur, günün görevlerini açar), 18:00 günlük raporun yorumu, Pazartesi 09:00 haftalık plan. Ajanlara görev verir; kendisi dışarıya hiçbir şey yapmaz.',
    rule: 'Worker servisinde çalışır; hangi hattan (Genesis ya da GM aboneliği) çalıştığı Ayarlar › Ajanlar’da yazar. Bütçesi dolunca durur, Telegram’a bildirim gider.',
  },
  temsilci: {
    role: 'Müşteri cevapları',
    duty: 'Müşteri sorusunu (Etsy mesajı, e-posta, ekran görüntüsü) okur, müşteri geçmişine ve bilgi bankasına bakar, İngilizce cevap taslağı ve Türkçe özetini yazar.',
    rule: 'Alıcı kişisel verisi Genesis Fast ve Genesis Think’e gitmez; bu rol Genesis Eye’da kalır. Ekran görüntüleri sunucuda şifreli tutulur.',
    output: 'T3 onaylı müşteri cevabı. Kanal bağlanana kadar "Onayla ve kopyala": mesajı Sercan gönderir.',
  },
  icerik: {
    role: 'İlan metinleri ve SEO',
    duty: 'İlan başlığı ve açıklaması, SEO önerileri, ürün kartlarından pazarlama metni. Genel Müdür’ün verdiği görevlerle çalışır.',
    output: 'T3 onaylı ilan metni değişikliği; Etsy bağlanınca Dağıtıcı uygular.',
  },
  denetci: {
    role: 'Kontrol',
    duty: 'Her taslağı Sercan’a gelmeden önce kontrol eder: teknik iddialar ürün kartıyla uyumlu mu, tarih vaadi var mı, platform dışı bağlantı var mı, politika ihlali var mı.',
    rule: 'Üretenin karşı model ailesiyle ve yalnız maskeli metinle (ad, e-posta, telefon, sipariş no silinmiş) çalışır; aynı model kendi hatasını göremez.',
  },
};

const STATE_LABEL: Record<NodeState, string> = {
  on: 'çalışıyor',
  wait: 'bekliyor',
  off: 'kapalı',
  need: 'senden',
  trial: 'deneme',
};

function agentBadge(agent: Agent): { state: NodeState; badge: string } {
  switch (agent.state) {
    case 'working':
      return { state: 'on', badge: 'çalışıyor' };
    case 'idle':
      return { state: 'on', badge: 'hazır' };
    case 'waiting':
      return { state: 'wait', badge: 'bekliyor' };
    case 'paused':
      return { state: 'off', badge: 'duraklatıldı' };
    default:
      return { state: 'need', badge: 'hata' };
  }
}

function budgetLine(agent: Agent): string {
  if (agent.budget.kind === 'genesis') {
    return `Genesis: ${formatUsdPair(agent.budget.spent, agent.budget.cap)} bu ay (tavan ${formatUsd(agent.budget.cap, 'auto')}).`;
  }
  const five = agent.budget.window5hPct === null ? 'henüz ölçülmedi' : `%${Math.round(agent.budget.window5hPct)}`;
  const week = agent.budget.weekPct === null ? 'henüz ölçülmedi' : `%${Math.round(agent.budget.weekPct)}`;
  return `GM aboneliği: 5 saatlik pencere ${five}, hafta ${week}; API karşılığı ${formatUsd(agent.budget.apiEquivalent, 'auto')} (nominal).`;
}

function agentNode(agent: Agent, tier: Tier): OrgNode {
  const copy = AGENT_COPY[agent.id];
  const { state, badge } = agentBadge(agent);
  const now =
    agent.state === 'waiting'
      ? `Bekliyor: ${agent.waitingFor ?? 'girdi'}${agent.waitingNote ? ` · ${agent.waitingNote}` : ''}`
      : agent.error
        ? `Hata: ${agent.error.message} (${agent.error.attempts} deneme)`
        : agent.todayLine;
  const details: [string, string][] = [['Görevi', copy?.duty ?? agent.task]];
  if (copy?.rule) details.push(['Kural', copy.rule]);
  if (copy?.output) details.push(['Çıktısı', copy.output]);
  details.push(['Model', `${agent.provider} · ${agent.model}`], ['Bütçe', budgetLine(agent)], ['Şu an', now]);
  if (agent.next) details.push(['Sonraki', agent.next.label]);
  return {
    id: agent.id,
    person: agent.id,
    tier,
    name: agent.name,
    role: copy?.role ?? agent.shortTask,
    state,
    badge,
    meta: `${agent.provider} · ${agent.model}`,
    details,
  };
}

/** The panel agent behind a department member, for its live model, budget and state (C2 shows four). */
const PANEL_OF: Readonly<Record<string, string>> = { temsilci: 'temsilci', 'icerik-seo': 'icerik', denetci: 'denetci' };

/** A department member as a chart node: a live agent keeps its model and budget lines, a planned role its duty. */
function memberNode(member: OrgMember, department: OrgDepartment, snapshot: Snapshot): OrgNode {
  const live = snapshot.agents.find((agent) => agent.id === PANEL_OF[member.id]);
  const boss = department.independent
    ? 'Sercan (bağımsız)'
    : member.id === department.manager?.id
      ? 'Genel Müdür'
      : (department.manager?.name ?? 'Genel Müdür');
  if (live !== undefined) {
    const node = agentNode(live, 2);
    return {
      ...node,
      id: member.id,
      role: member.role,
      details: [
        ['Görevi', member.duty ?? node.details[0]?.[1] ?? ''],
        ['Bağlı olduğu', boss],
        ...node.details.slice(1),
      ],
    };
  }
  const state: NodeState =
    member.state === 'working' || member.state === 'ready' || member.state === 'human' ? 'on' : 'off';
  const how = member.runtime === null ? 'insan' : (ORG_RUNTIME_LABELS[member.runtime] ?? member.runtime);
  const details: [string, string][] = [];
  if (member.duty !== null) details.push(['Görevi', member.duty]);
  details.push(['Bağlı olduğu', boss], ['Çalışma şekli', how]);
  if (member.state === 'planned') {
    details.push([
      'Durum',
      'Kurulacak: departmanın planı ve bütçesi onayına gelince açılır; o zamana kadar çalışmaz, para harcamaz.',
    ]);
  }
  return {
    id: member.id,
    person: member.id,
    tier: 2,
    name: member.name,
    role: member.role,
    state,
    badge: ORG_STATE_LABELS[member.state],
    meta: how,
    human: member.kind === 'human',
    details,
  };
}

function connectionState(connection: Connection | undefined): { state: NodeState; badge: string } {
  if (connection === undefined) return { state: 'off', badge: 'yok' };
  switch (connection.status) {
    case 'connected':
    case 'expiring':
      return { state: 'on', badge: 'bağlı' };
    case 'awaiting_sercan':
      return { state: 'need', badge: 'senden' };
    case 'applying':
      return { state: 'wait', badge: 'bekliyor' };
    case 'error':
      return { state: 'need', badge: 'hata' };
    case 'not_connected':
      return /kayıtlı/i.test(connection.statusNote)
        ? { state: 'wait', badge: 'izin bekliyor' }
        : { state: 'off', badge: 'bağlı değil' };
    default:
      return { state: 'off', badge: 'kapalı' };
  }
}

function connectionNode(
  id: string,
  tier: Tier,
  name: string,
  role: string,
  connection: Connection | undefined,
  details: [string, string][],
): OrgNode {
  const { state, badge } = connectionState(connection);
  return {
    id,
    tier,
    name,
    role,
    state,
    badge,
    meta: connection === undefined ? 'ayarlarda yok' : connection.meta,
    details: [
      ...details,
      ['Durum', connection === undefined ? 'Ayarlarda böyle bir bağlantı yok.' : `${connection.statusDetail}`],
      ['Nereden', 'Ayarlar › Bağlantılar'],
    ],
  };
}

function buildNodes(snapshot: Snapshot, settings: Settings | null): OrgNode[] {
  const connection = (id: string) => settings?.connections.find((item) => item.id === id);
  const nodes: OrgNode[] = [
    {
      id: 'sercan',
      person: 'sercan',
      tier: 0,
      name: 'Sercan',
      role: 'Sahip',
      state: 'on',
      badge: 'sen',
      human: true,
      meta: 'Onaylar, acil durdurma, sürüm yayını',
      details: [
        [
          'Görevi',
          'Her dış eylemi onaylar: T3 hızlı onay (10 sn geri alma), T4 basılı tutma veya Telegram’da iki adım (fiyat, indirim, iade, reklam, yeni kanal, tasarım teslimi). Satış planını onaylar.',
        ],
        ['Kanallar', 'Panel (kullanıcı adı ve şifre) ve Telegram botu.'],
      ],
    },
  ];
  const gm = snapshot.agents.find((agent) => agent.id === 'gm' || agent.budget.kind === 'claude_max');
  if (gm) nodes.push(agentNode(gm, 1));
  const stopped = snapshot.system.killSwitch.level !== 'none';
  nodes.push({
    id: 'exec',
    tier: 3,
    name: 'Dağıtıcı',
    role: 'Uygulayıcı (kod, ajan değil)',
    state: stopped ? 'off' : snapshot.system.dryRun ? 'trial' : 'on',
    badge: stopped ? 'durduruldu' : snapshot.system.dryRun ? 'deneme' : 'gerçek',
    meta: `Ayrı servis · ${snapshot.system.dryRun ? 'deneme modu açık' : 'dış yazma açık'} · 10 sn geri alma`,
    details: [
      [
        'Görevi',
        'Sadece onaylanmış işi uygular: Etsy ilan güncellemesi, kupon, site değişikliği, bildirim. Dış çağrıdan önce niyet kaydı yazar, sonucu hedeften geri okuyarak doğrular.',
      ],
      [
        'Güvenlik',
        'Onaydan 24 saat sonra uygulamaz. Onaydan sonra hedef değiştiyse iptal eder. Acil durdurma önce kaydedilir.',
      ],
      [
        'Şu an',
        stopped
          ? `Kapatma anahtarı açık (${snapshot.system.killSwitch.level})${snapshot.system.killSwitch.reason ? `: ${snapshot.system.killSwitch.reason}` : ''}.`
          : snapshot.system.dryRun
            ? 'Deneme modunda: hiçbir dış yazma yapmaz; Etsy işleri bağlantı gelene kadar iptal olur.'
            : 'Gerçek uygulamada.',
      ],
    ],
  });
  const about = settings?.about;
  nodes.push({
    id: 'panel',
    tier: 4,
    name: 'Panel ve API',
    role: 'twin.bmuworks.com',
    state: 'on',
    badge: 'canlı',
    meta: about
      ? `${about.version} · CI ${about.ci === 'passed' ? 'yeşil' : about.ci === 'running' ? 'koşuyor' : 'kırmızı'}`
      : 'Kullanıcı girişi · onaylar · ekip ve ayarlar',
    details: [
      [
        'Ne yapar',
        'Bugün, Onaylar, İstekler, Ekip, Leadler ve Ayarlar ekranları. T3 onayı, T4 basılı tutma, acil durdurma, bağlantı değerleri.',
      ],
      [
        'Güvenlik',
        'Şifreli bağlantı, kendi kullanıcı girişimiz, deneme sınırı ve kilit; iki adımlı doğrulama hazır, Sercan açınca devreye girer.',
      ],
      ['Sürüm', about ? `${about.version} (${about.commit})` : 'yükleniyor'],
    ],
  });
  nodes.push(
    connectionNode('telegram', 4, 'Telegram botu', 'Cep telefonu kanalı', connection('telegram'), [
      [
        'Ne yapar',
        'Ekran görüntüsünü alıp görev açar, onay kartlarını gönderir (T3 tek dokunuş, T4 iki adım ve 3 sn bekleme), günlük raporu iletir; /durum, /onaylar, /durdur, /devam, /butce.',
      ],
    ]),
    connectionNode('etsy', 4, 'Etsy', 'Mağaza verisi', connection('etsy'), [
      [
        'Ne yapar',
        'Sipariş, ilan ve istatistik okuma; onaylı ilan metnini Dağıtıcı yazar. Mağaza izni (OAuth) ayrı adımdır; yazma yetkisi öncesi iki adımlı doğrulama açılır.',
      ],
    ]),
    connectionNode('gmail', 4, 'Gmail (mağaza postası)', 'Müşteri soruları', connection('gmail'), [
      [
        'Ne yapar',
        'Gelen müşteri sorularını (GrabCAD, Etsy bildirimleri) okur; Temsilci taslak hazırlar, Sercan onaylar. Gönderim yalnız onayla, sonraki sürümde.',
      ],
    ]),
  );
  nodes.push({
    id: 'db',
    tier: 4,
    name: 'Veritabanı ve yayın hattı',
    role: 'Postgres 17 · GitHub',
    state: 'on',
    badge: 'canlı',
    meta: 'Her sürüm otomatik kurulur; yayın kapısı aynı commit’in yeşil CI’ını bekler',
    details: [
      [
        'Ne yapar',
        'Tüm kayıtlar, onay durumları, silinemez denetim zinciri; alıcı verisi pazarlamadan yalıtılmış. GitHub’da sürüm yayınlanınca sunucu imajları alır, göçleri uygular, servisleri başlatır, sağlıksızsa geri alır.',
      ],
      ['Eksik', 'Şifreli sunucu dışı yedek ve geri yükleme provası henüz kurulmadı.'],
    ],
  });
  return nodes;
}

interface Flow {
  title: string;
  tag: string;
  off?: boolean;
  steps: [string, string][];
  note: string | null;
}

function buildFlows(settings: Settings | null): Flow[] {
  const connection = (id: string) => settings?.connections.find((item) => item.id === id);
  const etsyOn = connection('etsy')?.status === 'connected';
  const gmailOn = connection('gmail')?.status === 'connected';
  const leadEngine = settings?.flags.find((flag) => flag.id === 'lead_scan')?.enabled ?? false;
  const outreach = settings?.flags.find((flag) => flag.id === 'outreach')?.enabled ?? false;
  return [
    {
      title: 'Müşteri mesajı',
      tag: 'T3',
      steps: [
        [
          gmailOn ? 'Soru Gmail’den kendiliğinden düşer' : 'Etsy’den mesaj gelir; ekran görüntüsünü Telegram’a atarsın',
          gmailOn ? 'Gmail okuyucu' : 'Sen',
        ],
        ['Görsel veya e-posta şifreli kaydedilir, görev açılır', 'Telegram botu, API'],
        ['Müşteri geçmişi ve bilgi bankasıyla cevap taslağı (EN + TR özet)', 'Mağaza Temsilcisi'],
        ['Teknik iddialar, vaatler, bağlantılar kontrol edilir', 'Denetçi'],
        ['"Onayla ve kopyala" veya Düzelt veya Reddet', 'Sen, panel veya Telegram'],
        [
          etsyOn ? 'Dağıtıcı gönderir, sonucu geri okur' : 'Cevabı Etsy’de sen gönderirsin; Etsy izni gelince Dağıtıcı',
          etsyOn ? 'Dağıtıcı' : 'Sen, sonra Dağıtıcı',
        ],
      ],
      note: gmailOn
        ? null
        : 'Gmail okuyucu bir sonraki sürümde; adres ve uygulama şifresi Ayarlar › Bağlantılar’dan girilir.',
    },
    {
      title: 'İlan metni',
      tag: 'T3',
      steps: [
        ['Sabah planında görev açılır (örnek: dönüşümü düşük ilan)', 'Genel Müdür'],
        ['Yeni başlık ve açıklama taslağı, farkı gösterilir', 'İçerik ve SEO'],
        ['Kontrol', 'Denetçi'],
        ['Onay, 10 sn geri alma', 'Sen'],
        [etsyOn ? 'Etsy’de günceller, sonucu geri okur' : 'Etsy izni gelince günceller; şimdi deneme', 'Dağıtıcı'],
      ],
      note: etsyOn ? null : 'Etsy mağaza izni (Bağla) gelene kadar Dağıtıcı bu işi uygulamaz.',
    },
    {
      title: 'Kupon, fiyat, iade',
      tag: 'T4',
      steps: [
        ['Öneri ve gerekçe (örnek: %15 kupon, 30 gün)', 'Genel Müdür'],
        ['Kontrol', 'Denetçi'],
        ['Detay ekranında 1,5 sn basılı tutma; Telegram’da özet ve 3 sn sonra ikinci onay', 'Sen'],
        ['Uygular; 24 saat geçtiyse uygulamaz', 'Dağıtıcı'],
      ],
      note: null,
    },
    {
      title: 'Günlük ritim',
      tag: 'takvim',
      steps: [
        ['07:00 veri toplama: siparişler, yorumlar, trafik, dünkü harcama', 'Worker'],
        ['07:30 sabah planı, görevler', 'Genel Müdür'],
        ['Gün içinde görevler ve onaylar', 'Ekip, sen'],
        ['18:00 günlük rapor Telegram’a, GM yorumuyla', 'Worker, Genel Müdür'],
        ['Pazartesi 09:00 haftalık plan', 'Genel Müdür'],
      ],
      note: 'Saatler Europe/Istanbul; ajanlar iş gelince çalışır, aradaki sessizlik normal.',
    },
    {
      title: 'Lead motoru',
      tag: leadEngine ? (outreach ? 'açık' : 'tarama açık') : 'kapalı',
      off: !leadEngine,
      steps: [
        ['Satış planı onayı, bayrak açılır', 'Sen'],
        ['Hücre hücre tarama, çok anahtar kelime, dört kaynak', 'Bölge Temsilcisi'],
        ['Kör doğrulama, iki kanıt', 'Doğrulayıcı'],
        ['Temas uygunluğu: ülke kuralı, izin kanıtı, durdurma listesi', 'Sistem'],
        ['İlk mail taslağı, senin onayınla şablon', 'Mail Yazarı'],
        [
          'Cevaplar sınıflanır; kelimeler, bölgeler ve metinler sonuçtan öğrenir',
          'Cevap Sınıflayıcı, haftalık öğrenme',
        ],
      ],
      note: leadEngine
        ? outreach
          ? null
          : 'Tarama açık, dış temas (outreach) kapalı: kimseye yazılmaz.'
        : 'Pilot verisi (88 hücre, 153 firma) Leadler sayfasında; motor plan onayına kadar kapalı, kimseyle iletişim kurulmadı.',
    },
  ];
}

function strip(snapshot: Snapshot, settings: Settings | null): { state: NodeState; text: string }[] {
  const counts = agentCounts(snapshot.agents);
  const pills: { state: NodeState; text: string }[] = [{ state: 'on', text: 'Panel ve API canlı' }];
  const stopped = snapshot.system.killSwitch.level !== 'none';
  pills.push(
    stopped
      ? { state: 'off', text: 'Kapatma anahtarı açık' }
      : snapshot.system.dryRun
        ? { state: 'trial', text: 'Dağıtıcı deneme modunda: dışarıya bir şey uygulamaz' }
        : { state: 'on', text: 'Dağıtıcı gerçek uygulamada' },
  );
  const agentText = [
    counts.working > 0 ? `${counts.working} çalışıyor` : null,
    counts.idle > 0 ? `${counts.idle} hazır` : null,
    counts.waiting > 0 ? `${counts.waiting} bekliyor` : null,
    counts.paused > 0 ? `${counts.paused} duraklatıldı` : null,
    counts.error > 0 ? `${counts.error} hata` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(', ');
  pills.push({
    state: counts.error > 0 ? 'need' : counts.working + counts.idle > 0 ? 'on' : 'wait',
    text: `Ajanlar: ${agentText || 'kayıt yok'}`,
  });
  if (settings) {
    const leadEngine = settings.flags.find((flag) => flag.id === 'lead_scan')?.enabled ?? false;
    pills.push(
      leadEngine
        ? { state: 'on', text: 'Satış motoru açık' }
        : { state: 'off', text: 'Satış motoru kapalı: plan onayı bekliyor' },
    );
    for (const id of ['telegram', 'etsy', 'gmail']) {
      const connection = settings.connections.find((item) => item.id === id);
      if (!connection) continue;
      const { state } = connectionState(connection);
      pills.push({ state, text: `${connection.name}: ${connection.statusNote}` });
    }
    const needs = settings.connections.filter((item) => item.status === 'awaiting_sercan').map((item) => item.name);
    if (needs.length > 0)
      pills.push({
        state: 'need',
        text: `Senden: ${needs.slice(0, 4).join(', ')}${needs.length > 4 ? ` +${needs.length - 4}` : ''}`,
      });
  } else {
    pills.push({ state: 'wait', text: 'Ayarlar yükleniyor' });
  }
  return pills;
}

export function Organization() {
  const { snapshot, settings } = usePanel();
  const { store: orgStore, state: org } = useOrganization();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | NodeState>('all');
  useEffect(() => {
    void orgStore.ensureLoaded();
  }, [orgStore]);
  if (!snapshot) return null;
  const departments = (org.view?.departments ?? []).filter((department) => department.code !== 'yonetim');
  const memberNodes = departments.flatMap((department) =>
    [department.manager, ...department.members]
      .filter((member): member is OrgMember => member !== null)
      .map((member) => ({ department: department.code, node: memberNode(member, department, snapshot) })),
  );
  const nodes = [...buildNodes(snapshot, settings), ...memberNodes.map((item) => item.node)];
  const selected = nodes.find((node) => node.id === selectedId) ?? null;
  const flows = buildFlows(settings);
  const dim = (node: OrgNode) => filter !== 'all' && node.state !== filter;
  const nodeButton = (node: OrgNode) => (
    <button
      key={node.id}
      type="button"
      className={`org-node org-node--${node.state}${node.human ? ' org-node--human' : ''}${dim(node) ? ' org-node--dim' : ''}`}
      aria-pressed={node.id === selectedId}
      onClick={() => setSelectedId(node.id === selectedId ? null : node.id)}
    >
      <span className="org-node__name">{node.name}</span>
      <span className="org-node__st">{node.badge}</span>
      <span className="org-node__meta">{node.role}</span>
      <span className="org-node__meta">{node.meta}</span>
    </button>
  );
  return (
    <>
      <section className="section org" aria-labelledby="org-h">
        <SectionHeader
          id="org-h"
          title="Organizasyon şeması"
          right={<span className="section-header__aside">canlı</span>}
        />
        <ul className="org-strip" aria-label="Özet durum">
          {strip(snapshot, settings).map((pill) => (
            <li key={pill.text} className={`org-pill org-pill--${pill.state}`}>
              {pill.text}
            </li>
          ))}
        </ul>
        <fieldset className="org-filters">
          <legend className="sr-only">Süz</legend>
          {FILTERS.map((item) => (
            <button key={item.id} type="button" aria-pressed={filter === item.id} onClick={() => setFilter(item.id)}>
              {item.label}
            </button>
          ))}
        </fieldset>
        <div className="org-chart">
          {TIERS.map((tier) =>
            tier.tier === 2 ? (
              <div key={tier.tier} className="org-tier" data-label={tier.label}>
                {org.view === null ? (
                  <p className="placeholder-text">
                    {org.error === null ? 'Departmanlar yükleniyor' : `Departmanlar okunamadı: ${org.error}`}
                  </p>
                ) : (
                  <div className="org-depts">
                    {departments.map((department) => (
                      <section
                        key={department.code}
                        className={`org-dept${department.independent ? ' org-dept--independent' : ''}`}
                        aria-label={department.name}
                      >
                        <header className="org-dept__head">
                          <h3>{department.name}</h3>
                          <span className="org-dept__note">
                            {department.independent
                              ? 'bağımsız · yalnız sana bağlı'
                              : `müdür: ${department.manager?.name ?? 'yok'}`}
                          </span>
                        </header>
                        <p className="org-dept__purpose ink-2">{department.purpose}</p>
                        <div className="org-dept__people">
                          {memberNodes
                            .filter((item) => item.department === department.code)
                            .map((item) => nodeButton(item.node))}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div key={tier.tier} className="org-tier" data-label={tier.label}>
                <div className={tier.one ? 'org-row org-row--one' : 'org-row'}>
                  {nodes.filter((node) => node.tier === tier.tier).map(nodeButton)}
                </div>
              </div>
            ),
          )}
        </div>
        {selected ? (
          <article className="org-detail" aria-live="polite">
            <header className="org-detail__head">
              <div>
                <p className="org-eyebrow">
                  {selected.role} · {STATE_LABEL[selected.state]}
                </p>
                <h3>{selected.name}</h3>
              </div>
              <button type="button" className="org-close" onClick={() => setSelectedId(null)}>
                Kapat
              </button>
            </header>
            <dl>
              {selected.details.map(([term, text]) => (
                <div key={term}>
                  <dt>{term}</dt>
                  <dd>{text}</dd>
                </div>
              ))}
            </dl>
            {selected.person ? (
              <Link to={`/ekip/kisi/${selected.person}`} className="text-btn org-detail__link">
                Yaptıkları
              </Link>
            ) : null}
          </article>
        ) : (
          <p className="placeholder-text">Bir kutuya dokununca ayrıntısı burada açılır.</p>
        )}
      </section>
      <section className="section org-flows" aria-labelledby="org-flows-h">
        <SectionHeader id="org-flows-h" title="İş akışları, üstten alta" />
        <div className="org-flowgrid">
          {flows.map((flow) => (
            <article key={flow.title} className="org-flow">
              <h3>
                {flow.title}
                <span className={`org-tag${flow.tag === 'T4' ? ' org-tag--t4' : flow.off ? ' org-tag--off' : ''}`}>
                  {flow.tag}
                </span>
              </h3>
              <ol>
                {flow.steps.map(([what, who], index) => (
                  <li key={what}>
                    <span className="mono org-flow__n">{String(index + 1).padStart(2, '0')}</span>
                    <span>
                      <b>{what}</b>
                      <span className="org-flow__who">{who}</span>
                    </span>
                  </li>
                ))}
              </ol>
              {flow.note ? <p className="org-flow__note ink-2">{flow.note}</p> : null}
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

/**
 * Ekip shows the team once (panel audit 25 Oct 2026, F16): the agent rows and the live flow stay on the page; the
 * pixel office and the org chart, which drew the same roles twice more, open behind one toggle.
 */
export function OrganizationToggle() {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <section className="section org-toggle" aria-labelledby={`${panelId}-h`}>
      <SectionHeader id={`${panelId}-h`} title="Organizasyon" />
      <button
        type="button"
        className="org-toggle__button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen(!open)}
      >
        {open ? 'Ofisi ve şemayı gizle' : 'Ofis planı ve organizasyon şeması'}
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} />
      </button>
      {open ? (
        <div id={panelId}>
          <Office />
          <Organization />
        </div>
      ) : null}
    </section>
  );
}
